import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { userAtom } from 'src/atoms/user';
import Page from '../components/Page';
import { Icon } from '@iconify/react';
import locationIcon from '@iconify/icons-akar-icons/location';
import emailOutline from '@iconify/icons-eva/email-outline';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';

import {
  CircularProgress,
  Grid,
  Typography,
  Chip,
  Backdrop,
  Dialog,
  DialogTitle,
  Slide,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Switch,
  FormControlLabel,
  Button,
  ButtonGroup,
  Stack
} from '@material-ui/core';
import moment from 'moment';
import { capitalize } from 'lodash';
import { useNavigate } from 'react-router';
import { snackbarAtom } from 'src/atoms/snackbar';
import axios from 'axios';
import ProfilePic from 'src/components/ProfilePic';
import { getImage } from 'src/services/images.services';
import { queryClient } from 'src';
import BeneficiaryImage from 'src/components/BeneficiaryImage';
import { downloadReport } from 'src/services/employee.service';

const loaderStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
  height: '10%',
  width: '10%',
  display: 'grid',
  placeItems: 'center center'
};

const columns = [
  {
    field: 'name',
    headerName: 'Name',
    width: 250,
    type: 'string',
    renderCell: ({ row }) => {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          <BeneficiaryImage beneficiary={row} />
          <Typography variant="">{row.name}</Typography>
        </Stack>
      );
    }
  },
  {
    field: 'age',
    headerName: 'Age',
    width: 150,
    type: 'number'
  },
  {
    field: 'gender',
    headerName: 'Gender',
    width: 150,
    type: 'singleSelect',
    valueOptions: ['Male', 'Female']
  },
  {
    field: 'relationship',
    headerName: 'Relationship',
    width: 200,
    type: 'singleSelect',
    valueOptions: ['Spouse', 'Child']
  }
];

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Profile = ({ employee }) => {
  /* eslint-disable no-unused-vars */
  const [user, setUser] = useRecoilState(userAtom);
  const { id } = useParams();
  const {
    isLoading: loading,
    isError: errorLoadingUser,
    data,
    isFetching,
    error
  } = useQuery(
    ['user_profile', employee ? id : user.id],
    async () => await axios(`/employees/${employee ? id : user.id}`).then((res) => res.data),
    {
      enabled: !!user
    }
  );
  const [imageKey, setImageKey] = useState(null);
  useEffect(() => {
    if (data) {
      setImageKey(data.profile_picture);
      setActive(data.active);
      if (!employee) {
        setUser((prev) => {
          return {
            ...prev,
            ...data
          };
        });
      }
    }
  }, [data]);
  const {
    isLoading: loadingProfilePic,
    isError: isErrorProfilePic,
    data: profilePic,
    error: errorProfilePic
  } = useQuery(
    ['profile_pic', imageKey],
    async () => {
      return await getImage(imageKey);
    },
    {
      enabled: !!user && !!data && !!imageKey
    }
  );

  useEffect(() => {
    if (profilePic && !employee) {
      setUser((prev) => {
        return {
          ...prev,
          profile_picture: profilePic
        };
      });
    }
  }, [profilePic]);

  const refetch = async (newUrl) => {
    const oldKey = imageKey;
    setImageKey(newUrl);
    queryClient.invalidateQueries('profile_pic');
    queryClient.removeQueries(['profile_pic', oldKey]);
    queryClient.invalidateQueries('user_profile');
  };
  const [snackbar, setSnackbar] = useRecoilState(snackbarAtom);
  const [active, setActive] = useState(false);
  const theme = useTheme();
  const match = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);
  const beneficiaries = data?.beneficiaries?.map((ben) => {
    return {
      id: ben.id,
      name: ben.name,
      relationship: capitalize(ben.relationship),
      gender: capitalize(ben.gender),
      age: moment().diff(ben.birthdate, 'years', false),
      profile_picture: ben.profile_picture
    };
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries('user_profile');
    };
  }, []);

  if (loading) {
    return <CircularProgress style={loaderStyle} />;
  } else if (errorLoadingUser || error) {
    setSnackbar({ severity: 'error', open: true, message: (errorLoadingUser || error).message });
    return <Typography variant="h3">Error Loading User</Typography>;
  }
  return (
    <div
      style={{
        paddingInline: match ? '20px' : '0px'
      }}
    >
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog
        open={openDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          setOpenDialog(false);
        }}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{'Delete Employee?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Deleting employee will result in deleting their beneficiaries as well as their requests.
            <br />
            Please note, this process is irreversible and you will lose that data forever!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={async () => {
              // Delete
              setOpenDialog(false);
              try {
                setOpenBackdrop(true);
                const userId = employee ? id : user.id;
                const { status } = await axios({
                  url: `/employees/${userId}`,
                  method: 'DELETE'
                });
                if (status !== 200) {
                  throw new Error("Couldn't delete employee");
                }
                navigate('../');
              } catch (err) {
                console.log(err);
              } finally {
                setOpenBackdrop(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Page title={data.name + ' | Incorta Care'}>
        <Grid container>
          <Grid item container xs={12} md={8}>
            <Grid item style={{ position: 'relative', width: '150px', height: '150px' }}>
              {isErrorProfilePic ? (
                <p>Something went wrong: {errorProfilePic.message}</p>
              ) : (
                <ProfilePic
                  src={profilePic}
                  loading={loadingProfilePic || isFetching}
                  refetch={refetch}
                  employee={employee}
                />
              )}
            </Grid>
            <Grid item style={{ marginLeft: '20px' }}>
              {/* Data */}
              <Stack spacing={1}>
                <Stack alignItems={'center'} direction={'row'} gap={'5px'}>
                  <Typography variant="h3">{data.name}</Typography>
                  <Typography variant="h6" color="textSecondary">
                    ({data.employeeId})
                  </Typography>

                  <Chip
                    label={capitalize(data.role)}
                    variant="outlined"
                    style={{
                      marginLeft: '20px',
                      color: 'white',
                      background: 'linear-gradient(to right bottom, #21314D, #4A6086)'
                    }}
                  />
                </Stack>
                <Typography variant="h5">
                  {moment().diff(data.birthdate, 'years', false)} Years Old
                </Typography>
                <Typography variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
                  <Icon
                    icon={locationIcon}
                    style={{ marginRight: '5px', transform: 'scale(1.3)' }}
                  />
                  {capitalize(data.office)} Office
                </Typography>
                <Link
                  href={`mailto:${data.email}`}
                  underline="hover"
                  color="InfoText"
                  style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}
                >
                  <Icon
                    icon={emailOutline}
                    style={{ marginRight: '10px', transform: 'scale(1.5)' }}
                  />
                  Contact
                </Link>
                {(!employee || user.role === 'ADMIN') && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
                      {employee ? 'Download report:' : 'Download my report:'}
                    </Typography>
                    <ButtonGroup variant="outlined" size="small">
                      {[
                        ['xlsx', 'Excel'],
                        ['pdf', 'PDF']
                      ].map(([format, label]) => (
                        <Button
                          key={format}
                          onClick={async () => {
                            try {
                              setOpenBackdrop(true);
                              await downloadReport({
                                id: employee ? id : undefined,
                                name: data.name,
                                format
                              });
                            } catch (err) {
                              setSnackbar({ severity: 'error', open: true, message: err.message });
                            } finally {
                              setOpenBackdrop(false);
                            }
                          }}
                        >
                          {label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Stack>
                )}
                {user.role === 'ADMIN' ? (
                  <Typography variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
                    Balance: ${Number(data.balance).toFixed(2)}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
          {user.role === 'ADMIN' && (
            <Grid item container xs={12} md={4} alignItems="center" justifyContent="center">
              <Stack spacing={2} direction={match ? 'row' : 'column'}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={active}
                      onChange={async (e) => {
                        try {
                          setActive(e.target.checked);
                          setOpenBackdrop(true);
                          const userId = employee ? id : user.id;
                          const { status } = await axios({
                            url: `/employees/${userId}`,
                            method: 'PATCH',
                            data: {
                              main_data: {
                                active: e.target.checked
                              }
                            }
                          });
                          if (status !== 200) {
                            throw new Error("Couldn't change activation state of the user");
                          }
                        } catch (err) {
                          setSnackbar({ severity: 'error', open: true, message: err.message });
                          console.log(err);
                        } finally {
                          setOpenBackdrop(false);
                        }
                      }}
                    />
                  }
                  label={active ? 'Active' : 'Disabled'}
                />
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    if (employee) navigate('./update');
                    else {
                      navigate(`../employees/${user.id}/update`);
                    }
                  }}
                >
                  Update User
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setOpenDialog(true);
                  }}
                >
                  Delete User
                </Button>
              </Stack>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} md={12} marginBottom={10}></Grid>
        <Typography variant="h4" style={{ marginBottom: '15px' }}>
          Beneficiaries
        </Typography>
      </Page>

      <DataGrid
        rows={beneficiaries}
        autoHeight
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[5, 10, 15, 20]}
        components={{
          Toolbar: CustomToolbar
        }}
        disableSelectionOnClick
      />
    </div>
  );
};
export default Profile;
